<script>
import ArrowLeftIcon from '@/assets/icons/arrow-left.svg';

// https://stackoverflow.com/a/4819886
const isTouchDevice = () =>
  'ontouchstart' in window ||
  navigator.maxTouchPoints > 0 ||
  navigator.msMaxTouchPoints > 0;

const isRtl = () => document.querySelector('body').dir === 'rtl';

export default {
  name: 'Carousel',
  components: {
    ArrowLeftIcon,
  },
  props: {
    perPage: {
      type: [Number, Function],
      default: 0,
    },
    beginningIndex: {
      type: Number,
      default: 0,
    },
    itemWidth: {
      type: Number,
      default: 100,
    },
  },
  data() {
    return {
      isTouchDevice: false,
      showNextButton: false,
      showPrevButton: false,
      mouseDownPos: 0,
      isScrolling: false,
    };
  },
  mounted() {
    this.$nextTick(() => {
      this.isTouchDevice = isTouchDevice();
      this.calculateItemsPerPage();
      window.addEventListener('resize', this.onPageResize);
      window.addEventListener('deviceorientation', this.onPageResize);

      this.scrollToItem(this.beginningIndex);
    });
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.onPageResize);
    window.removeEventListener('deviceorientation', this.onPageResize);
    window.removeEventListener('mousemove', this.onMouseMove, true);
    window.removeEventListener('mouseup', this.onMouseUp, true);
    clearTimeout(this.pageResizeTimeout);
    clearTimeout(this.contentScrollTimeout);
  },
  methods: {
    calculateItemsPerPage() {
      try {
        const itemsLength = this.$slots.default.length || 1;
        let perPage =
          typeof this.perPage === 'function' ? this.perPage() : this.perPage;
        if (perPage > 0) {
          this.$refs.innerContent.style.width = `${(itemsLength * 95) /
            perPage}%`;
        }
      } catch (_e) {}
      this.onContentScroll();
    },
    onPageResize() {
      clearTimeout(this.pageResizeTimeout);
      this.pageResizeTimeout = setTimeout(this.calculateItemsPerPage, 100);
    },
    onContentScroll() {
      clearTimeout(this.contentScrollTimeout);
      this.contentScrollTimeout = setTimeout(() => {
        const { content } = this.$refs;
        const { scrollLeft, offsetWidth, scrollWidth } = content;
        this.showPrevButton = Math.abs(scrollLeft) > 8;
        this.showNextButton =
          scrollWidth - (Math.abs(scrollLeft) + offsetWidth) > 8;
      }, 100);
    },
    scrollBy(changeAmount = 1) {
      try {
        const directionModifier = isRtl() ? -1 : 1;
        const itemEl = document.querySelector('.carousel__inner-content > *');
        const itemWidth = itemEl.scrollWidth || 130;
        const scrollAmount = itemWidth * Math.abs(changeAmount);

        const duration = 300;
        const startTime = performance.now();
        const startScroll = this.$refs.content.scrollLeft;
        const targetScroll =
          startScroll + scrollAmount * changeAmount * directionModifier;

        const doScroll = currentTime => {
          const elapsed = currentTime - startTime;
          const progress = Math.min(elapsed / duration, 1);

          const easeProgress = 1 - Math.pow(1 - progress, 3);
          this.$refs.content.scrollLeft =
            startScroll + (targetScroll - startScroll) * easeProgress;

          if (progress < 1) {
            requestAnimationFrame(doScroll);
          }
        };

        requestAnimationFrame(doScroll);
      } catch (_e) {}
    },
    scrollToItem(index) {
      try {
        const items = document.querySelectorAll('.carousel__inner-content > *');
        items?.[index]?.scrollIntoViewIfNeeded?.();
      } catch (_e) {}
    },
    onMouseDown(e) {
      if (e.which === 1) {
        this.mouseDownPos = e.pageX + this.$refs.content.scrollLeft;
        window.addEventListener('mousemove', this.onMouseMove, true);
        window.addEventListener('mouseup', this.onMouseUp, true);
      }
    },
    onMouseMove(e) {
      this.isScrolling = true;
      this.$refs.content.scrollTo(this.mouseDownPos - e.pageX, 0);
      e.preventDefault();
    },
    onMouseUp(e) {
      if (this.isScrolling) {
        this.isScrolling = false;
        e.preventDefault();
      }
      window.removeEventListener('mousemove', this.onMouseMove, true);
      window.removeEventListener('mouseup', this.onMouseUp, true);
    },
  },
};
</script>

<template>
  <div
    class="carousel"
    :class="[isTouchDevice && 'touch', isScrolling && 'scrolling']"
    @dragstart.prevent=""
  >
    <span
      class="carousel__arrow-key carousel__arrow-key--prev"
      :class="!showPrevButton && 'disabled'"
      @click="scrollBy(-1)"
    >
      <ArrowLeftIcon />
    </span>
    <div
      ref="content"
      class="carousel__content"
      @scroll="onContentScroll"
      @mousedown.stop="onMouseDown"
    >
      <div ref="innerContent" class="carousel__inner-content">
        <slot />
      </div>
    </div>
    <span
      class="carousel__arrow-key carousel__arrow-key--next"
      :class="!showNextButton && 'disabled'"
      @click="scrollBy(1)"
    >
      <ArrowLeftIcon />
    </span>
  </div>
</template>

<style lang="scss">
$arrow-btn-size: 30px;

@mixin carousel-arrow($dir) {
  #{$dir}: 0;
  > svg {
    transform: rotate(if($dir == 'right', 180deg, 0));
  }
}

.carousel {
  min-width: 100%;
  position: relative;
  overflow: visible;

  display: flex;
  align-items: center;
  gap: 1rem;

  * {
    user-select: none;
  }

  &__content {
    overflow-x: hidden;
    // scroll-behavior: smooth;
    overflow-y: visible;
    height: inherit;
    min-height: inherit;
    max-height: inherit;
    scroll-padding: 0 50px;
  }

  &.touch &__content {
    overflow-x: scroll;
    margin: 0;
  }

  &__inner-content {
    padding: 4px;
    display: flex;
    flex-direction: row;
    align-items: flex-start;
    justify-content: flex-start;
    overflow: visible;
    width: fit-content; // will change on runtime if needed

    > * {
      flex-shrink: 1;
      margin: 4px;
    }
  }

  &.scrolling &__inner-content > * {
    pointer-events: none;
  }

  &.scrolling &__content {
    scroll-behavior: initial;
  }

  &__arrow-key {
    display: block;
    height: $arrow-btn-size;
    min-height: $arrow-btn-size;
    max-height: $arrow-btn-size;
    width: $arrow-btn-size;
    min-width: $arrow-btn-size;
    max-width: $arrow-btn-size;
    border-radius: 8px;
    line-height: $arrow-btn-size;
    text-align: center;
    background: #fff;
    cursor: pointer;
    padding: 0;
    overflow: hidden;
    z-index: 1;
    transition: opacity 0.2s;
    border-radius: 50%;
    // box-shadow: rgba(0, 0, 0, 0.25) 0px 54px 55px,
    //   rgba(0, 0, 0, 0.12) 0px -12px 30px, rgba(0, 0, 0, 0.12) 0px 4px 6px,
    //   rgba(0, 0, 0, 0.17) 0px 12px 13px, rgba(0, 0, 0, 0.09) 0px -3px 5px;
    background-color: #eee;

    &.disabled {
      opacity: 0;
      pointer-events: none;
    }

    &:hover {
      > svg {
        fill: #333;
      }
    }

    > svg {
      width: $arrow-btn-size * 0.4;
      height: $arrow-btn-size * 0.4;
      fill: #464646;
      transition: fill 0.1s;
      vertical-align: middle;
    }

    @include rtl {
      &--next {
        @include carousel-arrow('left');
      }

      &--prev {
        @include carousel-arrow('right');
      }
    }

    @include ltr {
      &--next {
        @include carousel-arrow('right');
      }

      &--prev {
        @include carousel-arrow('left');
      }
    }
  }

  &.touch &__arrow-key {
    display: none;
  }
}
</style>
